<template>
  <FlashNotifications ref="flash" />

  <ConfirmModal :open="confirmOff" title="Disconnect OpenAI?" confirm-text="Disconnect" :saving="store.working"
    message="Your saved key is deleted from Timelydo and AI features stop working until you connect a key again. Your key stays valid at OpenAI: delete it there too if you no longer want it."
    @cancel="confirmOff = false" @confirm="disconnect" />

  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">
    <!-- Header -->
    <div class="bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-3 px-4 sm:px-6 py-3.5">
        <RouterLink to="/integrations" data-turbo-prefetch="false" aria-label="Back to integrations"
          class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>
        </RouterLink>
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">OpenAI</h1>
        <span v-if="store.loaded" class="ml-1 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full" :class="STATUS[status].pill">
          <span class="w-1.5 h-1.5 rounded-full" :class="STATUS[status].dot"></span>
          {{ STATUS[status].label }}
        </span>
      </div>
    </div>

    <div class="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4 pb-24">
      <!-- Loading -->
      <div v-if="!store.loaded && store.loading" class="animate-pulse space-y-4" aria-hidden="true">
        <div class="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
        <div class="h-56 bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
      </div>

      <!-- Error -->
      <div v-else-if="store.error && !store.loaded" class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
        <button type="button" @click="store.fetch()" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Try again</button>
      </div>

      <template v-else>
        <!-- Intro -->
        <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-6 py-5 flex items-start gap-4">
          <img src="/images/integrations/openai.png" alt="" class="w-12 h-12 rounded-xl flex-shrink-0">
          <div>
            <h2 class="font-semibold text-gray-900 dark:text-white">Use your own OpenAI account</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Timelydo uses your OpenAI API key for the AI features you choose to use, such as tailoring your resume to a job.
              The key is stored encrypted and is never shown again. OpenAI bills the usage to your own account.
              <a href="/help-center/job-tracker/connect-openai" target="_blank" rel="noopener noreferrer" data-turbo="false"
                class="font-semibold text-blue-600 dark:text-blue-400 hover:underline">How to connect it</a>
              or <a href="/help-center/job-tracker/turn-on-ai#cost" target="_blank" rel="noopener noreferrer" data-turbo="false"
                class="font-semibold text-blue-600 dark:text-blue-400 hover:underline">what AI costs</a>.
            </p>
          </div>
        </div>

        <!-- Connected: the key, and what can be done with it -->
        <div v-if="store.hasKey" class="bg-white dark:bg-[#1C2128] rounded-xl border px-6 py-5"
          :class="store.stale ? 'border-red-200 dark:border-red-900/50' : 'border-gray-100 dark:border-gray-700'">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-3">
            <div class="min-w-0">
              <p class="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">API key</p>
              <p class="mt-1 font-mono text-sm text-gray-900 dark:text-white" data-testid="key-hint">sk-••••••••{{ store.connection.key_hint }}</p>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                <template v-if="store.stale">OpenAI stopped accepting this key.</template>
                <template v-else>Checked with OpenAI {{ ago(store.connection.verified_at) }}. Connected {{ ago(store.connection.connected_at) }}.</template>
              </p>
            </div>

            <div class="ml-auto flex flex-wrap items-center gap-2">
              <button v-if="!store.stale" type="button" @click="check" :disabled="store.working" :class="SECONDARY">
                <svg v-if="checking" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"/></svg>
                {{ checking ? 'Checking...' : 'Check key again' }}
              </button>
              <button type="button" @click="replacing = !replacing" :disabled="store.working" :class="SECONDARY">{{ replacing ? 'Cancel' : 'Replace key' }}</button>
              <button type="button" @click="confirmOff = true" :disabled="store.working"
                class="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60">Disconnect</button>
            </div>
          </div>

          <p v-if="store.stale" role="alert" class="mt-4 text-sm text-red-600 dark:text-red-400">
            The integration is switched off. Paste a working key below to turn it back on.
          </p>
        </div>

        <!-- The key form: first connect, replace, or a key that stopped working -->
        <form v-if="!store.hasKey || replacing || store.stale" @submit.prevent="connect"
          class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-6 py-5">
          <h2 class="font-semibold text-gray-900 dark:text-white">{{ store.hasKey ? 'Save a new key' : 'Connect your OpenAI account' }}</h2>
          <ol class="mt-2 mb-4 space-y-1 text-sm text-gray-500 dark:text-gray-400 list-decimal pl-5">
            <li>
              Create a key on the
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" class="font-semibold text-blue-600 dark:text-blue-400 hover:underline">OpenAI API keys page</a>.
            </li>
            <li>Paste it here. We check it with OpenAI first, and only save it if OpenAI accepts it.</li>
          </ol>

          <label for="openai-key" class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">API key</label>
          <div class="relative">
            <input id="openai-key" ref="keyInput" v-model="apiKey" :type="reveal ? 'text' : 'password'" autocomplete="off" autocapitalize="off" spellcheck="false"
              placeholder="sk-..." maxlength="400" :aria-invalid="Boolean(formError)" :aria-describedby="formError ? 'openai-key-error' : undefined"
              class="w-full h-10 pl-3 pr-11 font-mono text-sm rounded-lg border bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2"
              :class="formError ? 'border-red-300 dark:border-red-800 focus:ring-red-500/30' : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500/40 focus:border-blue-400'">
            <button type="button" @click="reveal = !reveal" :aria-label="reveal ? 'Hide key' : 'Show key'" :aria-pressed="reveal"
              class="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
              <svg v-if="!reveal" class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/></svg>
              <svg v-else class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="1.7" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/></svg>
            </button>
          </div>
          <p v-if="formError" id="openai-key-error" role="alert" class="mt-2 text-sm text-red-600 dark:text-red-400">{{ formError }}</p>

          <div class="mt-4 flex items-center justify-between gap-3">
            <p class="text-xs text-gray-400 dark:text-gray-500">The key is sent over HTTPS, stored encrypted, and never shown again.</p>
            <button type="submit" :disabled="store.working || !apiKey.trim()"
              class="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-semibold text-white shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0">
              <svg v-if="connecting" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"/></svg>
              {{ connecting ? 'Checking with OpenAI...' : 'Validate and connect' }}
            </button>
          </div>
        </form>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { useOpenAiStore } from 'stores/openai.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import ConfirmModal from 'controllers/vue_components/shared/ConfirmModal.vue'

const SECONDARY = 'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed'
const STATUS = {
  connected: { label: 'Connected', pill: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400', dot: 'bg-green-500' },
  stale: { label: 'Key stopped working', pill: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400', dot: 'bg-red-500' },
  off: { label: 'Not connected', pill: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
}

const store = useOpenAiStore()
const flash = ref(null)
const keyInput = ref(null)
const apiKey = ref('')
const reveal = ref(false)
const replacing = ref(false)
const confirmOff = ref(false)
const formError = ref('')
const connecting = ref(false)
const checking = ref(false)

const status = computed(() => {
  if (store.stale) return 'stale'
  return store.connection.connected ? 'connected' : 'off'
})

// "3 days ago" style.
const UNITS = [['year', 31536000], ['month', 2592000], ['week', 604800], ['day', 86400], ['hour', 3600], ['minute', 60]]
function ago(time) {
  if (!time) return 'just now'
  const seconds = Math.round((new Date(time) - Date.now()) / 1000)
  if (Math.abs(seconds) < 60) return 'just now'
  const [unit, size] = UNITS.find(([, length]) => Math.abs(seconds) >= length)
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(Math.round(seconds / size), unit)
}

async function connect() {
  formError.value = ''
  connecting.value = true
  const result = await store.connect(apiKey.value.trim())
  connecting.value = false

  if (!result.ok) {
    formError.value = result.message
    nextTick(() => keyInput.value?.focus())
    return
  }
  apiKey.value = '' // the key is not kept in the page once it is saved
  reveal.value = false
  replacing.value = false
  flash.value?.show('OpenAI connected', 'notice')
}

async function check() {
  checking.value = true
  const result = await store.check()
  checking.value = false
  flash.value?.show(result.message, result.ok ? 'notice' : 'alert')
}

async function disconnect() {
  const result = await store.disconnect()
  confirmOff.value = false
  if (result.ok) {
    replacing.value = false
    formError.value = ''
  }
  flash.value?.show(result.message, result.ok ? 'notice' : 'alert')
}

onMounted(() => store.fetch())
</script>
